import { useMemo } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useUserSetupStore } from '@sib/auth';
import { useNotification } from '@sib/notifications';
import { tracking } from '@sib/tracking';
import { useSorting } from '@spendbrain/common/hooks';
import { FileUploadStore } from '@spendbrain/stores/fileUploadStore';
import { FileItem, FileStatus } from '@spendbrain/types/files';
import {
  useUploadToBlobStorage,
  useBatchUpload,
} from '@spendbrain/utils/blob-storage-upload';
import { DROPZONE_CONFIG, useOnDropHandler } from '@spendbrain/utils/files';

interface UseUploadingFilesListProps {
  fileUploadStore: FileUploadStore;
  closeFileManager?: () => void;
  dropzoneConfig?: Partial<DropzoneOptions>;
}

export const useUploadingFilesList = ({
  fileUploadStore,
  closeFileManager,
  dropzoneConfig,
}: UseUploadingFilesListProps) => {
  const { t } = useTranslation();
  const {
    files,
    addFiles,
    getFiles,
    onFilesUploaded,
    updateFile,
    selectAll,
    removeUploadedFiles,
    getAreFilesUploading,
    removeFile,
  } = fileUploadStore;

  const areFilesUploading = getAreFilesUploading();
  const { organizationName, organizationId } = useUserSetupStore();
  const { sortedItems: sortedFiles, toggleSortOrder } = useSorting<FileItem>(
    files,
    fileItem => fileItem.file.name
  );
  const { onDrop } = useOnDropHandler({
    addFiles,
  });
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...(dropzoneConfig || DROPZONE_CONFIG),
    onDrop,
  });

  const { open } = useNotification();

  const fileUploadCallbacks = useMemo(
    () => ({
      onProgress: (fileItem: FileItem, progress: number) =>
        updateFile({
          ...fileItem,
          status: FileStatus.Uploading,
          progress,
        }),
      onSuccess: (fileItem: FileItem) => {
        tracking.track('Completed file upload', {
          fileName: fileItem.file.name,
          organizationId,
        });
        updateFile({ ...fileItem, status: FileStatus.Uploaded });
      },
      onError: (fileItem: FileItem, error?: string) => {
        tracking.track('Failed file upload', {
          error,
          fileName: fileItem.file.name,
          organizationId,
        });
        updateFile({
          ...fileItem,
          status: FileStatus.Error,
          errorMessage: error,
        });
      },
    }),
    [updateFile, organizationId]
  );
  const { uploadFileToBlobStorage: uploadSingleFileToBlobStorage } =
    useUploadToBlobStorage(fileUploadCallbacks);

  const { generateBatchId, checkForUploadStatus } = useBatchUpload({
    files,
    ...fileUploadCallbacks,
  });
  const uploadFiles = async () => {
    closeFileManager?.();
    open?.({
      type: 'info',
      message: t('fileManager.startUploadMessage'),
      description: t('fileManager.startUploadDescription'),
    });
    const filesToUpload = files.filter(
      file => file.status !== FileStatus.Error
    );
    if (!filesToUpload.length) return;

    const batchUploadId = generateBatchId();

    filesToUpload.forEach(fileItem => {
      updateFile({
        ...fileItem,
        status: FileStatus.Uploading,
        progress: 0,
      });
    });

    const uploadAllFiles = filesToUpload.map(fileItem =>
      uploadSingleFileToBlobStorage(fileItem, {
        totalFiles: filesToUpload.length,
        batchId: batchUploadId,
      })
    );

    await Promise.all(uploadAllFiles);

    await checkForUploadStatus(batchUploadId);

    const currentFiles = getFiles();

    const uploadedCount = currentFiles.filter(
      file => file.status === FileStatus.Uploaded
    ).length;
    const failedFiles = currentFiles.filter(
      file => file.status === FileStatus.Error
    );
    const failedCount = failedFiles.length;

    if (failedCount > 0) {
      open?.({
        type: 'error',
        message: t('fileManager.uploadFailedMessage'),
        description:
          uploadedCount > 0
            ? t('fileManager.uploadFailedPartialDescription', {
                uploadedCount,
                failedCount,
              })
            : t('fileManager.uploadFailedAllFailedDescription', {
                failedCount,
              }),
      });
    } else if (uploadedCount > 0) {
      open?.({
        type: 'success',
        message: t('fileManager.uploadSuccessMessage'),
        description: t('fileManager.uploadSuccessDescription', {
          uploadedCount,
        }),
      });
      onFilesUploaded?.();
      closeFileManager?.();
    }

    removeUploadedFiles();
  };

  const hasErrors = files.some(file => file.status === FileStatus.Error);

  const anySelected = files.some(file => file.selected);

  const allSelected =
    files.length > 0 && files.every(fileItem => fileItem.selected);

  const toggleSelectAllFiles = () => {
    selectAll(!allSelected);
  };

  const areThereAnyFiles = files.length > 0;

  return {
    files,
    sortedFiles,
    isDragActive,
    hasErrors,
    allSelected,
    areThereAnyFiles,
    areFilesUploading,
    anySelected,
    organizationName,
    toggleSelectAllFiles,
    uploadFiles,
    toggleSortOrder,
    getRootProps,
    getInputProps,
    updateFile,
    removeFile,
  };
};
